import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { CancelBookingConfirmDialog } from '@/components/bookings/CancelBookingConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';

export interface BookingActionsMenuProps {
  bookingId: string;
  lessonId: string;
  pendingInCluster: string[];
  confirmedInCluster: string[];
  completedInCluster: string[];
  cancelledInCluster?: string[];
  cancellableIds: string[];
  campSessionClusterSize?: number;
  hasValidSubscription: boolean;
  subscriptionTooltip?: string;
  onViewDetails: (bookingId: string) => void;
  onUpdateStatus: (
    bookingIds: string[],
    status: 'pending' | 'confirmed' | 'completed'
  ) => void | Promise<void | boolean>;
  onCancelBookings: (bookingIds: string[]) => void | Promise<void | boolean>;
  onRestoreBookings?: (bookingIds: string[]) => void | Promise<void | boolean>;
  onMoveToAnother?: () => void;
  ariaLabel?: string;
  triggerVariant?: 'outline' | 'ghost';
  triggerSize?: 'icon' | 'sm';
}

export function BookingActionsMenu({
  bookingId,
  lessonId,
  pendingInCluster,
  confirmedInCluster,
  completedInCluster,
  cancelledInCluster = [],
  cancellableIds,
  campSessionClusterSize,
  hasValidSubscription,
  subscriptionTooltip,
  onViewDetails,
  onUpdateStatus,
  onCancelBookings,
  onRestoreBookings,
  onMoveToAnother,
  ariaLabel = 'Booking actions',
  triggerVariant = 'outline',
  triggerSize = 'icon',
}: BookingActionsMenuProps) {
  const navigate = useNavigate();
  const multi = (campSessionClusterSize ?? 0) > 1;
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [pendingCancelIds, setPendingCancelIds] = useState<string[]>([]);
  const [cancelling, setCancelling] = useState(false);

  const handleConfirmCancel = async () => {
    if (pendingCancelIds.length === 0) return;
    setCancelling(true);
    try {
      await onCancelBookings(pendingCancelIds);
      setCancelDialogOpen(false);
      setPendingCancelIds([]);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size={triggerSize}
          variant={triggerVariant}
          className={
            triggerSize === 'icon'
              ? 'h-8 w-8 shrink-0 border-border/80 bg-background shadow-sm hover:bg-muted/60'
              : 'h-8 w-8 p-0'
          }
          aria-label={ariaLabel}
        >
          <MoreVertical className="h-4 w-4" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={4} className="w-56">
        <DropdownMenuItem
          onSelect={() => {
            navigate('/admin/calendar', { state: { highlightLessonId: lessonId } });
          }}
        >
          View Lesson
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onViewDetails(bookingId)}>View booking details</DropdownMenuItem>

        {pendingInCluster.length > 0 && (
          <>
            {!hasValidSubscription && subscriptionTooltip ? (
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                {subscriptionTooltip}
              </DropdownMenuLabel>
            ) : null}
            <DropdownMenuItem
              disabled={!hasValidSubscription}
              onSelect={() => {
                if (hasValidSubscription) {
                  void onUpdateStatus(pendingInCluster, 'confirmed');
                }
              }}
            >
              Confirm{multi ? ' all pending' : ''}
            </DropdownMenuItem>
          </>
        )}

        {confirmedInCluster.length > 0 && (
          <>
            {!hasValidSubscription && subscriptionTooltip ? (
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                {subscriptionTooltip}
              </DropdownMenuLabel>
            ) : null}
            <DropdownMenuItem
              disabled={!hasValidSubscription}
              onSelect={() => {
                if (hasValidSubscription) {
                  void onUpdateStatus(confirmedInCluster, 'completed');
                }
              }}
            >
              Mark Complete{multi ? ' (confirmed)' : ''}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!hasValidSubscription}
              onSelect={() => {
                if (hasValidSubscription) {
                  void onUpdateStatus(confirmedInCluster, 'pending');
                }
              }}
            >
              Mark Pending{multi ? ' (confirmed)' : ''}
            </DropdownMenuItem>
          </>
        )}

        {completedInCluster.length > 0 && (
          <>
            {!hasValidSubscription && subscriptionTooltip ? (
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                {subscriptionTooltip}
              </DropdownMenuLabel>
            ) : null}
            <DropdownMenuItem
              disabled={!hasValidSubscription}
              onSelect={() => {
                if (hasValidSubscription) {
                  void onUpdateStatus(completedInCluster, 'confirmed');
                }
              }}
            >
              Undo Complete{multi ? ' (completed)' : ''}
            </DropdownMenuItem>
          </>
        )}

        {cancelledInCluster.length > 0 && onRestoreBookings ? (
          <>
            {!hasValidSubscription && subscriptionTooltip ? (
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                {subscriptionTooltip}
              </DropdownMenuLabel>
            ) : null}
            <DropdownMenuItem
              disabled={!hasValidSubscription}
              onSelect={() => {
                if (hasValidSubscription) {
                  void onRestoreBookings(cancelledInCluster);
                }
              }}
            >
              Restore enrollment{multi ? 's' : ''}
            </DropdownMenuItem>
          </>
        ) : null}

        {onMoveToAnother ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onMoveToAnother}>Move to another lesson</DropdownMenuItem>
          </>
        ) : null}

        {cancellableIds.length > 0 && (
          <>
            <DropdownMenuSeparator />
            {!hasValidSubscription && subscriptionTooltip ? (
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                {subscriptionTooltip}
              </DropdownMenuLabel>
            ) : null}
            <DropdownMenuItem
              disabled={!hasValidSubscription}
              className="text-destructive focus:bg-destructive/10 focus:text-destructive"
              onSelect={() => {
                if (hasValidSubscription) {
                  setPendingCancelIds(cancellableIds);
                  setCancelDialogOpen(true);
                }
              }}
            >
              Cancel Booking{cancellableIds.length > 1 ? 's' : ''}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
    <CancelBookingConfirmDialog
      open={cancelDialogOpen}
      onOpenChange={(open) => {
        if (!cancelling) {
          setCancelDialogOpen(open);
          if (!open) setPendingCancelIds([]);
        }
      }}
      bookingCount={pendingCancelIds.length}
      loading={cancelling}
      onConfirm={handleConfirmCancel}
    />
    </>
  );
}
